import React from 'react';
import '../style.css';
import { Button, Group, Dialog } from '@economic/taco';
import { OnMount } from '~/app/shared/components/OnMount';
import { t } from '~/app/shared/components/l10n/l10n';
import { Field } from '~/app/shared/components/Form';
import { DisplayMessages } from './DisplayMessages';
import { InvoicingMessage, InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { useEmailOptionsPdfSelected, useEmailOptionsRecipientsSelected } from '../../invoices/utils';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { SendAsEmail } from './SendBy';
import { SalesDialogFormWithEmailOptionsDrawer } from './SalesDialogFormWithEmailOptionsDrawer';
import { ResendFormData, ResendMultipleInvoicesFormData } from '../types';
import { EmailOrEInvoiceSendingLimitExceededAlert } from '../../../shared/EmailOrEInvoiceSendingLimitExceededAlert';

function useRequiredFieldsValidation(skipAttachmentOptionsValidation = false): InvoicingMessage[] {
    const requiredFieldsMessages = [] as InvoicingMessage[];
    // check if email options make sense for sending order.
    const isPdfSelected = useEmailOptionsPdfSelected();
    // check if at least one recipient fields is entered
    const isRecipientSelected = useEmailOptionsRecipientsSelected();

    if (!isPdfSelected && !skipAttachmentOptionsValidation) {
        requiredFieldsMessages.push({ message: t`FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR`, type: InvoicingMessageType.Error });
    }

    if (!isRecipientSelected) {
        requiredFieldsMessages.push({
            message: t`FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR`,
            type: InvoicingMessageType.Error,
        });
    }

    return requiredFieldsMessages;
}

interface ResendDialogProps {
    title: string;
    information?: ResendFormData | ResendMultipleInvoicesFormData;
    loading: boolean;
    hideAttachmentOptions?: boolean;
    onResend: (values: any) => Promise<any>;
    onHide: () => void;
    open: boolean;
}

export function ResendDialog(props: ResendDialogProps) {
    const { title, information, loading, hideAttachmentOptions, onResend, onHide, open } = props;

    return (
        <SalesDialogFormWithEmailOptionsDrawer
            loading={loading}
            ariaLabel={title}
            open={open}
            onHide={onHide}
            className="book-and-send-dialog form h-[500px]"
            size="xs"
            hideRemember
            hideAttachmentOptions={hideAttachmentOptions}
            initialData={information}
            onSubmit={onResend}
            updates={[emailTemplateUpdater(false)]}>
            {(form, drawer) =>
                information && (
                    <>
                        <OnMount run={() => !drawer.open && drawer.toggle()} />
                        <ResendDialogContent
                            title={title}
                            information={information}
                            skipAttachmentOptionsValidation={hideAttachmentOptions}
                            onResend={() => form.submit()}
                            onHide={onHide}
                        />
                    </>
                )
            }
        </SalesDialogFormWithEmailOptionsDrawer>
    );
}

interface ResendDialogContentProps {
    title: string;
    information: ResendFormData | ResendMultipleInvoicesFormData;
    skipAttachmentOptionsValidation?: boolean;
    onResend: () => void;
    onHide: () => void;
}

function ResendDialogContent(props: ResendDialogContentProps) {
    const { title, information, skipAttachmentOptionsValidation, onResend, onHide } = props;
    const requiredFieldValidationMessages = useRequiredFieldsValidation(skipAttachmentOptionsValidation);
    const messages = [...(information.messages || []), ...requiredFieldValidationMessages];

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>{title}</Dialog.Title>

            <SendAsEmail title={t`FORM_RESEND_EMAIL`} />

            <EmailTemplatePicker templates={information.templates} />

            <div className="overflow-auto grow">
                {information.exceededSendingLimit && <EmailOrEInvoiceSendingLimitExceededAlert />}
                <DisplayMessages messages={messages} />
            </div>

            <Field.Hidden name="sendByType" value={Number(SendByType.undefined)} />

            <Dialog.Footer>
                <Group>
                    <Button type="submit" onClick={onHide}>
                        {t`FORM_RESEND_BUTTON_CANCEL`}
                    </Button>
                    <Button
                        appearance="primary"
                        type="submit"
                        disabled={information.exceededSendingLimit || !!requiredFieldValidationMessages.length}
                        onClick={onResend}>
                        {t`FORM_RESEND_BUTTON_SEND`}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
